/**
 * @fileoverview The plant card's Care strip (B136, design.md rev 12): directly under the weather caption, a tinted rounded-square
 * strip (FactStrip) with the leaf, "Care", the server's two facts ("likes 65-85°F · water about weekly") and a bold chevron. A
 * tap unfolds the plant's temperatures, watering and feeding right under it (the About body, PlantAboutBody: grows best, hurt
 * below, water, feed), next to the weather they belong with, and "Hide" shuts it. Care is free reading, so it has no lock.
 *
 * The facts are the brief's (care.line, written by the server from the About data and the water plan); the tray's rows are
 * GET /plant-about's, read only while the tray is open. The app works out none of it. Whether the tray is open is the card's
 * one-tray-at-a-time state, so the card holds it and gives it here.
 *
 * Where: the plant card (PlantCard), under WeekCaption and above the tiles; an archived card shows it too (no facts, the tray
 * reads GET /plant-about).
 * API: GET /plant-about/:plantObjId (through PlantAboutBody, only while open).
 * Words (en/es): plants.card.care, plants.card.tiles.hide.
 *
 * @module CareStrip
 */

import React from 'react';
import { Leaf } from 'lucide-react';
import { FactStrip } from '@/shared/components/ui/FactStrip';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { TrayHide } from '@/shared/components/ui/TrayHide';
import { PlantAboutBody } from './PlantAboutSheet';

/** What the strip is given. */
interface CareStripProps {
  /** The plant. */
  plantObjId: number;
  /** Its one name, for the tray. */
  plantName: string;
  /** Its kind as a name (names.kindName), or null. */
  kindName: string | null;
  /** The two facts the brief sends ("likes 65-85°F · water about weekly"); null when the brief has none (an archived plant). */
  line: string | null;
  /** Whether the tray is open (the card's one open tray). */
  open: boolean;
  /** Opens or shuts the tray. */
  onToggle: () => void;
}

/**
 * The Care strip and, when open, its tray.
 *
 * @param props - CareStripProps.
 */
export const CareStrip: React.FC<CareStripProps> = ({ plantObjId, plantName, kindName, line, open, onToggle }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <div className="px-4 pb-0.5">
      <FactStrip icon={<Leaf />} open={open} onClick={onToggle}>
        <span className="shrink-0">{t('plants.card.care')}</span>
        {line && <span className="min-w-0 truncate font-medium text-muted-foreground">{`· ${line}`}</span>}
      </FactStrip>
      {open && (
        <div className="mt-1.5 rounded-xl bg-background px-3 py-2.5">
          <PlantAboutBody plantObjId={plantObjId} plantName={plantName} kindName={kindName} enabled />
          <TrayHide className="mt-1" onClick={onToggle}>
            {t('plants.card.tiles.hide')}
          </TrayHide>
        </div>
      )}
    </div>
  );
};
